import { FileExtensionType } from '~/enums/FileExtensionType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { intlService } from '~/i18n/IntlService';
import { fetchEverest } from '~/redux/api/everest/baseQuery';
import { ServiceRequestConfig } from '~/services/createService';
import { pilot } from '~/services/serviceInstances';
import { TicketDocument } from '~/types/Ticket';
import { Uuid } from '~/types/Uuid';

import { testIsAxiosError } from '../../utils';

interface Params {
  ticketId: Uuid;
  documentId: Uuid;
  versionId: Uuid;
}
export const resolveTicketDocument = async ({
  documentId,
  ticketId,
  versionId,
}: Params): Promise<{ file: File }> => {
  const documentResult = await fetchEverest({
    url: `tickets/${ticketId}/document`,
    rtkQueryOptions: { showErrorToast: false },
  });
  if (documentResult.error) {
    throw documentResult.error;
  }
  const ticketDocument = documentResult.data as TicketDocument;
  let file;
  switch (ticketDocument.fileType) {
    case FileExtensionType.Doc:
    case FileExtensionType.Docx:
      //TODO: Implement fetching SFDT when DocumentViewer supports EJ2 Doc Editor.
      break;
    case FileExtensionType.Jpg:
    case FileExtensionType.Jpeg:
    case FileExtensionType.Pdf:
    case FileExtensionType.Png:
    case FileExtensionType.Tiff:
      file = await getTicketPDFDocument({
        ticketVersionId: versionId,
        documentId,
      });
      break;
    default:
      break;
  }

  if (!file) {
    throw new Error(
      intlService.getIntl().formatMessage({
        id: 'resolveTicketDocument.downloadFailed',
        defaultMessage: 'Failed to download document',
        description: 'Error message when ticket document download fails',
      }),
    );
  }

  return {
    file,
  };
};

interface Request {
  ticketVersionId: Uuid;
  documentId: Uuid;
}
export const getTicketPDFDocument = async ({
  ticketVersionId,
  documentId,
}: Request): Promise<File | undefined> => {
  let file: File | undefined = undefined;
  try {
    file = await pilot.get('/document/download/ocr-pdf', {
      params: { tag: ticketVersionId },
      responseType: 'blob',
    });
  } catch (e: unknown) {
    if (
      testIsAxiosError(e) &&
      e.response?.status === HttpStatusCodeType.NotFound
    ) {
      try {
        const versionResult = await fetchEverest({
          url: `/documents/${documentId}/versions/${ticketVersionId}/download/original`,
          rtkQueryOptions: {
            showErrorToast: false,
            responseType: 'blob',
          } as unknown as ServiceRequestConfig,
        });
        if (versionResult.error) {
          throw versionResult.error;
        }
        file = versionResult.data as File;
      } catch {
        throw new Error(
          intlService.getIntl().formatMessage({
            id: 'resolveTicketDocument.downloadFailed',
            defaultMessage: 'Failed to download document',
            description: 'Error message when ticket document download fails',
          }),
        );
      }
    }
  }
  return file;
};
